import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Modal,
  Tag,
  Tooltip,
  Row,
  Col,
} from "antd";
import {
  SearchOutlined,
  UserAddOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import { Patient, CreatePatientRequest } from "../../types/Billing";
import PatientForm from "./PatientForm";
import dayjs from "dayjs";
import {
  fetchPatientsStart,
  deletePatientStart,
  createPatientStart,
  updatePatientStart,
  clearError,
} from "../../store/slices/billingSlice";

const PatientsList: React.FC = () => {
  const dispatch = useDispatch();
  const { patients, loading, error } = useSelector(
    (state: RootState) => state.billing
  );
  const [searchText, setSearchText] = useState("");
  const [modalVisible, setModalVisible] = useState(false);
  const [editingPatient, setEditingPatient] = useState<Patient | null>(null);
  const [filteredPatients, setFilteredPatients] = useState<Patient[]>([]);

  useEffect(() => {
    dispatch(fetchPatientsStart());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (searchText && Array.isArray(patients)) {
      const filtered = patients.filter(
        (patient) =>
          patient.name.toLowerCase().includes(searchText.toLowerCase()) ||
          patient.phone.includes(searchText) ||
          patient.patientId.toLowerCase().includes(searchText.toLowerCase())
      );
      setFilteredPatients(filtered);
    } else {
      setFilteredPatients(Array.isArray(patients) ? patients : []);
    }
  }, [patients, searchText]);

  const handleCreate = () => {
    setEditingPatient(null);
    setModalVisible(true);
  };

  const handleEdit = (patient: Patient) => {
    setEditingPatient(patient);
    setModalVisible(true);
  };

  const handleDelete = (patientId: string) => {
    Modal.confirm({
      title: "Delete Patient",
      content:
        "Are you sure you want to delete this patient? This action cannot be undone.",
      okText: "Yes, Delete",
      okType: "danger",
      onOk: () => {
        dispatch(deletePatientStart(patientId));
      },
    });
  };

  const handleSubmit = (patientData: CreatePatientRequest) => {
    if (editingPatient) {
      dispatch(
        updatePatientStart({
          id: editingPatient._id,
          patientData,
        })
      );
    } else {
      dispatch(createPatientStart(patientData));
    }
    setModalVisible(false);
    setEditingPatient(null);
  };

  const handleCancel = () => {
    setModalVisible(false);
    setEditingPatient(null);
  };

  const getGenderColor = (gender: string) => {
    switch (gender) {
      case "male":
        return "blue";
      case "female":
        return "pink";
      case "other":
        return "purple";
      default:
        return "default";
    }
  };

  const columns = [
    {
      title: "Patient ID",
      dataIndex: "patientId",
      key: "patientId",
      width: 120,
      sorter: (a: Patient, b: Patient) =>
        a.patientId.localeCompare(b.patientId),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a: Patient, b: Patient) => a.name.localeCompare(b.name),
      render: (name: string, record: Patient) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          <div style={{ fontSize: "12px", color: "#666" }}>
            {record.age} years
          </div>
        </div>
      ),
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      width: 100,
      render: (gender: string) => (
        <Tag color={getGenderColor(gender)}>
          {gender ? gender.charAt(0).toUpperCase() + gender.slice(1) : "N/A"}
        </Tag>
      ),
      filters: [
        { text: "Male", value: "male" },
        { text: "Female", value: "female" },
        { text: "Other", value: "other" },
      ],
      onFilter: (value: any, record: Patient) => record.gender === value,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 130,
    },
    {
      title: "Address",
      dataIndex: "address",
      key: "address",
      ellipsis: {
        showTitle: false,
      },
      render: (address: string) => (
        <Tooltip placement="topLeft" title={address}>
          {address}
        </Tooltip>
      ),
    },
    {
      title: "Registered",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date: string) => dayjs(date).format("DD MMM YYYY"),
      sorter: (a: Patient, b: Patient) =>
        dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (text: any, record: Patient) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => {
                Modal.info({
                  title: "Patient Details",
                  width: 600,
                  content: (
                    <div style={{ marginTop: "16px" }}>
                      <p>
                        <strong>Patient ID:</strong> {record.patientId}
                      </p>
                      <p>
                        <strong>Name:</strong> {record.name}
                      </p>
                      <p>
                        <strong>Age:</strong> {record.age} years
                      </p>
                      <p>
                        <strong>Gender:</strong> {record.gender}
                      </p>
                      <p>
                        <strong>Phone:</strong> {record.phone}
                      </p>
                      <p>
                        <strong>Address:</strong> {record.address}
                      </p>
                      <p>
                        <strong>Registered:</strong>{" "}
                        {dayjs(record.createdAt).format("DD MMM YYYY, hh:mm A")}
                      </p>
                      <p>
                        <strong>Last Updated:</strong>{" "}
                        {dayjs(record.updatedAt).format("DD MMM YYYY, hh:mm A")}
                      </p>
                    </div>
                  ),
                });
              }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record._id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="page-panel">
        <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 8 }}>
          <Col xs={24} sm={16} md={18}>
            <Input
              placeholder="Search by name, phone, or patient ID"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              size="small"
            />
          </Col>
          <Col xs={24} sm={8} md={6}>
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={handleCreate}
              size="small"
              style={{ width: "100%" }}
            >
              Add Patient
            </Button>
          </Col>
        </Row>

        <Table
          className="dense-table"
          size="small"
          columns={columns}
          dataSource={filteredPatients}
          rowKey="_id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            size: "small",
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} patients`,
          }}
          scroll={{ x: 800 }}
        />
      </div>

      <Modal
        title={editingPatient ? "Edit Patient" : "Add New Patient"}
        open={modalVisible}
        onCancel={handleCancel}
        footer={null}
        width={600}
      >
        <PatientForm
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          initialValues={editingPatient || undefined}
          loading={loading}
        />
      </Modal>
    </div>
  );
};

export default PatientsList;
